@if (suggestion(); as s) {
    <article class="detail">

        <!-- ── HEADER ─────────────────────────────────────────── -->
        <header class="d-head">
            <div class="d-head-main">
                <div class="d-eyebrow">
                    <span class="chip tone-info">AI FINISHER</span>
                    @if (s.handoffId != null) {
                        <span class="d-handoff mono">handoff #{{ s.handoffId }}</span>
                    }
                </div>
                <h1 class="d-ref mono">{{ s.winOlsReference || ('ai-suggestion-' + s.id) }}</h1>
                <div class="d-status-row">
                    <span class="chip" [ngClass]="'tone-' + approvalTone(s.approvalStatus)">{{ s.approvalStatus || 'PENDING' }}</span>
                    <span class="chip" [ngClass]="'tone-' + processingTone(s.processingStatus)">processing · {{ s.processingStatus || '—' }}</span>
                    <span class="chip tone-neutral">{{ s.suggestionType || 'tune' }}</span>
                </div>
            </div>
            <div class="d-actions">
                @if (isPending()) {
                    <button class="btn btn-approve" [disabled]="busy()" (click)="approve.emit()">
                        <i class="bi bi-check2"></i> Approve
                    </button>
                    <button class="btn btn-reject" [disabled]="busy()" (click)="startReject()">
                        <i class="bi bi-x-lg"></i> Reject
                    </button>
                }
                <button class="btn btn-ghost" [disabled]="busy()" (click)="download.emit()">
                    <i class="bi bi-download"></i> Download
                </button>
            </div>
        </header>

        <!-- inline reject panel -->
        @if (rejecting()) {
            <div class="reject-box">
                <label>Rejection feedback <span>— recorded as training signal</span></label>
                <textarea [(ngModel)]="rejectReason" rows="2"
                          placeholder="Why is this file not acceptable? (optional)"></textarea>
                <div class="reject-actions">
                    <button class="btn btn-reject" [disabled]="busy()" (click)="confirmReject()">Confirm reject</button>
                    <button class="btn btn-ghost" (click)="cancelReject()">Cancel</button>
                </div>
            </div>
        }

        <!-- ── STAT STRIP ─────────────────────────────────────── -->
        <section class="stat-strip">
            <div class="stat" [ngClass]="'tone-' + confidenceTone()">
                <span class="stat-label">Confidence</span>
                <span class="stat-value">{{ confidencePct() != null ? confidencePct() + '%' : '—' }}</span>
                <div class="bar"><div class="bar-fill" [style.width.%]="confidencePct() ?? 0"></div></div>
            </div>
            <div class="stat">
                <span class="stat-label">Bytes changed</span>
                <span class="stat-value mono">{{ bytesChanged() != null ? (bytesChanged() | number) : '—' }}</span>
            </div>
            <div class="stat">
                <span class="stat-label">Versions</span>
                <span class="stat-value">{{ versions().length || '—' }}</span>
            </div>
            <div class="stat">
                <span class="stat-label">Model</span>
                <span class="stat-value mono sm">{{ model() || '—' }}</span>
            </div>
        </section>

        <!-- ── ENGINEER FLAGS ─────────────────────────────────── -->
        @if (engineerFlags().length) {
            <section class="card card-warn">
                <div class="card-head">
                    <i class="bi bi-exclamation-triangle"></i>
                    <h2>Engineer must check</h2>
                    <span class="count">{{ engineerFlags().length }}</span>
                </div>
                <ul class="flag-list">
                    @for (f of engineerFlags(); track $index) {
                        <li>{{ f }}</li>
                    }
                </ul>
            </section>
        }

        <!-- ── RATIONALE ──────────────────────────────────────── -->
        <section class="card">
            <div class="card-head"><i class="bi bi-chat-square-text"></i><h2>AI rationale</h2></div>
            <p class="rationale">{{ s.rationale || '—' }}</p>
        </section>

        <!-- ── IDENTITY + BUNDLE ──────────────────────────────── -->
        <div class="grid-2">
            <section class="card">
                <div class="card-head"><i class="bi bi-fingerprint"></i><h2>Identity</h2></div>
                <dl class="kv">
                    <div><dt>Suggestion ID</dt><dd class="mono">#{{ s.id }}</dd></div>
                    <div><dt>Import ID</dt><dd class="mono">{{ s.importId != null ? '#' + s.importId : '—' }}</dd></div>
                    <div><dt>Pure Original ID</dt><dd class="mono">{{ s.pureOriginalId != null ? '#' + s.pureOriginalId : '—' }}</dd></div>
                    <div><dt>Handoff ID</dt><dd class="mono">{{ s.handoffId != null ? '#' + s.handoffId : '—' }}</dd></div>
                    <div><dt>WinOLS reference</dt><dd class="mono">{{ s.winOlsReference || '—' }}</dd></div>
                    <div><dt>Suggestion type</dt><dd>{{ s.suggestionType || '—' }}</dd></div>
                </dl>
            </section>

            <section class="card">
                <div class="card-head"><i class="bi bi-box-seam"></i><h2>Version bundle</h2></div>
                @if (versions().length) {
                    <div class="chip-row">
                        @for (v of versions(); track $index) {
                            <span class="chip tone-info mono">{{ v }}</span>
                        }
                    </div>
                } @else {
                    <p class="muted">No version names recorded.</p>
                }
                <dl class="kv">
                    <div><dt>Bundle reference</dt><dd class="mono truncate">{{ s.bundleBlob || '—' }}</dd></div>
                </dl>
                <button class="btn btn-ghost sm" [disabled]="busy()" (click)="download.emit()">
                    <i class="bi bi-download"></i> Download bundle (.zip)
                </button>
            </section>
        </div>

        <!-- ── DIFF + CHECKSUM ────────────────────────────────── -->
        <div class="grid-2">
            <section class="card">
                <div class="card-head">
                    <i class="bi bi-file-earmark-diff"></i><h2>Diff</h2>
                    @if (diff()) {
                        <button class="link-btn" (click)="showRawDiff.set(!showRawDiff())">{{ showRawDiff() ? 'Hide raw' : 'Raw JSON' }}</button>
                    }
                </div>
                @if (diff()) {
                    <dl class="kv">
                        <div><dt>Bytes changed</dt><dd class="mono">{{ bytesChanged() != null ? (bytesChanged() | number) : '—' }}</dd></div>
                    </dl>
                    @if (transfers().length) {
                        <div class="sub">Donor transfers</div>
                        <ul class="mini-list">
                            @for (t of transfers(); track $index) { <li class="mono">{{ t }}</li> }
                        </ul>
                    }
                    @if (showRawDiff()) { <pre class="raw">{{ pretty(diff()) }}</pre> }
                } @else if (s.diffJson) {
                    <pre class="raw">{{ s.diffJson }}</pre>
                } @else {
                    <p class="muted">No diff recorded.</p>
                }
            </section>

            <section class="card">
                <div class="card-head">
                    <i class="bi bi-shield-check"></i><h2>Checksum proof</h2>
                    @if (checksum()) {
                        <button class="link-btn" (click)="showRawChecksum.set(!showRawChecksum())">{{ showRawChecksum() ? 'Hide raw' : 'Raw JSON' }}</button>
                    }
                </div>
                @if (checksum(); as cs) {
                    <div class="chip-row">
                        <span class="chip" [ngClass]="cs.applied ? 'tone-success' : 'tone-neutral'">
                            {{ cs.applied ? 'checksums applied' : 'not applied' }}
                        </span>
                    </div>
                    @if (cs.policy) { <p class="policy">{{ cs.policy }}</p> }
                    @if (showRawChecksum()) { <pre class="raw">{{ pretty(cs) }}</pre> }
                } @else if (s.checksumProofJson) {
                    <pre class="raw">{{ s.checksumProofJson }}</pre>
                } @else {
                    <p class="muted">No checksum proof recorded.</p>
                }
            </section>
        </div>

        <!-- ── LIFECYCLE ──────────────────────────────────────── -->
        <section class="card">
            <div class="card-head"><i class="bi bi-clock-history"></i><h2>Lifecycle</h2></div>
            <dl class="kv kv-3">
                <div><dt>Approval</dt><dd><span class="chip" [ngClass]="'tone-' + approvalTone(s.approvalStatus)">{{ s.approvalStatus || 'PENDING' }}</span></dd></div>
                <div><dt>Approver</dt><dd>{{ s.approver || '—' }}</dd></div>
                <div><dt>Approved at</dt><dd class="mono">{{ s.approvedAt ? (s.approvedAt | date:'dd MMM yyyy · HH:mm') : '—' }}</dd></div>
                <div><dt>Processing</dt><dd><span class="chip" [ngClass]="'tone-' + processingTone(s.processingStatus)">{{ s.processingStatus || '—' }}</span></dd></div>
                <div><dt>Processed at</dt><dd class="mono">{{ s.processedAt ? (s.processedAt | date:'dd MMM yyyy · HH:mm') : '—' }}</dd></div>
                <div><dt>Created</dt><dd class="mono">{{ s.dateCreated ? (s.dateCreated | date:'dd MMM yyyy · HH:mm') : '—' }}</dd></div>
                <div><dt>Updated</dt><dd class="mono">{{ s.dateUpdated ? (s.dateUpdated | date:'dd MMM yyyy · HH:mm') : '—' }}</dd></div>
            </dl>
            @if (s.rejectReason) { <div class="alert alert-bad"><strong>Rejected:</strong> {{ s.rejectReason }}</div> }
            @if (s.errorMessage) { <div class="alert alert-bad"><strong>Error:</strong> {{ s.errorMessage }}</div> }
        </section>

        <!-- ── AI DECISION LOG ────────────────────────────────── -->
        <section class="card">
            <div class="card-head">
                <i class="bi bi-list-check"></i><h2>AI decision log</h2>
                @if (aiWork(); as w) {
                    @if (w.steps.length) { <span class="count">{{ w.steps.length }}</span> }
                }
                @if (totalTokens() != null) { <span class="tokens mono">{{ totalTokens() | number }} tok</span> }
            </div>

            @if (aiWork(); as w) {
                @if (w.steps.length > 0) {
                    <ol class="timeline">
                        @for (step of w.steps; track step.id) {
                            <li class="tl-item" [ngClass]="'tone-' + phaseTone(step.phase)">
                                <div class="tl-rail"><span class="tl-dot"></span></div>
                                <div class="tl-body">
                                    <div class="tl-top">
                                        <span class="chip" [ngClass]="'tone-' + phaseTone(step.phase)">{{ step.phase || 'STEP' }}</span>
                                        <span class="tl-seq mono">#{{ step.seq }}</span>
                                        <span class="spacer"></span>
                                        @if (step.toolCalled) { <span class="tl-tool mono">{{ step.toolCalled }}</span> }
                                    </div>
                                    @if (step.decision) { <div class="tl-decision">{{ step.decision }}</div> }
                                    @if (step.rationale) { <div class="tl-rationale">{{ step.rationale }}</div> }
                                    @if (step.toolResultSummary) {
                                        <div class="tl-result"><i class="bi bi-arrow-return-right"></i>{{ step.toolResultSummary }}</div>
                                    }
                                    <div class="tl-meta">
                                        @if (step.model) { <span class="mono">{{ step.model }}</span> }
                                        @if (step.tokens != null) { <span class="mono">{{ step.tokens | number }} tok</span> }
                                        @if (step.dateCreated) { <span class="mono">{{ step.dateCreated | date:'HH:mm:ss' }}</span> }
                                        @if (step.toolArgsJson) {
                                            <button class="link-btn" (click)="toggleStep(step.seq)">{{ isStepOpen(step.seq) ? 'hide args' : 'args' }}</button>
                                        }
                                    </div>
                                    @if (step.toolArgsJson && isStepOpen(step.seq)) { <pre class="raw">{{ step.toolArgsJson }}</pre> }
                                </div>
                            </li>
                        }
                    </ol>
                } @else {
                    <p class="muted">No decision log was recorded for this import yet.</p>
                }
            } @else if (s.importId == null) {
                <p class="muted">This suggestion isn't linked to a client import, so there's no decision log.</p>
            } @else {
                <p class="muted">No decision log is available for this import.</p>
            }
        </section>

    </article>
} @else if (loading()) {
    <div class="detail-state">
        <div class="spinner"></div>
        <p>Loading suggestion…</p>
    </div>
} @else {
    <div class="detail-state">
        <i class="bi bi-robot empty-icon"></i>
        <h3>No suggestion selected</h3>
        <p>Pick a file from the queue to review what the AI produced.</p>
    </div>
}
